import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  InsightEntityType,
  RecommendationHolderType,
  RecommendationModel,
  RecommendationProcessModel,
  RecommendationStatus
} from "../../../models/recommendation.model";
import {CurrentSiteContextService} from "../../../../commons/angular/services/current-site-context.service";
import {RecommendationsComponentStore} from "../../state/recommendations-component.store";
import {filter, Observable, Subscription, take, finalize} from "rxjs";
import {PANEL_DATA, PanelService} from "../../../../commons/angular/services/panel.service";
import firebase from "firebase/compat/app";
import {
  AiAgentSelectComponent
} from "../../../../ai-agents/angular/components/ai-agent-select/ai-agent-select.component";
import {AiAgentModel} from "../../../../ai-agents/models/ai-agent.model";
import {RecommendationHttpService} from "../../services/recommendation-http.service";
import {
  RecommendationAlertsService
} from "../../services/recommendation-alerts.service";
import Unsubscribe = firebase.Unsubscribe;
import {Direction} from "@angular/cdk/bidi";
import {ProcessTrackingStatusEnum} from "../../../../commons/types/process-tracking-status.enum";
import {getRecommendationProcessAgentId} from "../../state/recommendation-process.functions";
import {RecommendationProcessChannelService} from "../../services/recommendation-process-channel.service";

interface RecommendationPanelLayoutInput {
  holderType: RecommendationHolderType;
  draftId: string;
  holderId: string;
  entityType: InsightEntityType;
  direction?: Direction;
  allowGenerateRecommendations: boolean;
  onGenerateRecommendations: (agent: AiAgentModel) => Promise<RecommendationProcessModel>;
}

interface RecommendationQueryParams {
  siteId: string;
  status: RecommendationStatus;
  recommendationHolderType: RecommendationHolderType;
  recommendationHolderId: string;
  entityType: InsightEntityType;
  entityId: string;
  sort: string;
  limit: number;
}

require('./recommendation-panel-layout.scss');
@Component({
  standalone: false,
  selector: 'app-recommendation-panel-layout',
  templateUrl: './recommendation-panel-layout.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RecommendationsComponentStore]
})
export class RecommendationPanelLayoutComponent implements OnInit, OnDestroy {
  @Input() onGenerateRecommendations: (agent: AiAgentModel) => Promise<RecommendationProcessModel>;
  @Output() onClose = new EventEmitter<any>();
  @Output() openRoutePanelsByIds = new EventEmitter<string[]>();

  recommendations$: Observable<RecommendationModel[]>;
  loading$: Observable<boolean>;
  cursor$: Observable<string>;

  private _siteId: string;
  private _holderId: string;
  private _draftId: string;
  private _queryParams: RecommendationQueryParams;
  private _holderType: RecommendationHolderType;
  private _panel: { afterClose$: Observable<any>; instance?: any } | null = null;
  private _subscriptions: Subscription[] = [];

  public processesMap: Map<string, { process: RecommendationProcessModel, unsubscribe: Unsubscribe }> = new Map();
  public isLoading: boolean = false;
  public generateRecommendationsAllowed: boolean = false;
  public direction: Direction = 'ltr';

  constructor(
    @Inject(RecommendationsComponentStore) private store: RecommendationsComponentStore,
    @Inject(RecommendationHttpService) private recommendationHttpService: RecommendationHttpService,
    @Inject(CurrentSiteContextService) private currentSiteContextService: CurrentSiteContextService,
    @Inject(PANEL_DATA) public data: RecommendationPanelLayoutInput,
    @Inject(PanelService) private panelService: PanelService,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
    @Inject(RecommendationAlertsService) private recommendationAlertsService: RecommendationAlertsService,
    @Inject(RecommendationProcessChannelService) private recommendationProcessChannelService: RecommendationProcessChannelService) {
    this.recommendations$ = this.store.recommendations$;
    this.loading$ = this.store.loading$;
    this.cursor$ = this.store.cursor$;
  }

  ngOnInit(): void {
    this._subscriptions.push(this.loading$
      .pipe(filter(v => v === false))
      .subscribe(() => {
        this.cdr.detectChanges();
      }));
    this.generateRecommendationsAllowed = this.data.allowGenerateRecommendations;
    this.direction = this.data.direction || 'ltr';
    this._siteId = this.currentSiteContextService.currentSiteId;
    this._holderId = this.data.holderId;
    this._draftId = this.data.draftId;
    this._holderType = this.data.holderType;
    this.onGenerateRecommendations = this.data.onGenerateRecommendations;
    this._queryParams = {
      siteId: this._siteId,
      status: RecommendationStatus.Active,
      recommendationHolderType: this._holderType,
      recommendationHolderId: this._holderId,
      entityType: this.data.entityType,
      entityId: this.data.draftId,
      sort: 'createdAt,desc',
      limit: 20
    };
    this.loadRecommendations();

    this._subscriptions.push(this.recommendations$.subscribe(() => {
      if (this.processesMap.has('triggerRule')) {
        this.processesMap.delete('triggerRule');
        this._panel?.instance?.updateProcessingAgents(Array.from(this.processesMap.keys()));
        this.cdr.detectChanges();
      }
    }));

    this._subscriptions.push(this.recommendationProcessChannelService.getChannelsValues()
      .pipe(filter((process: RecommendationProcessModel) => this.processesMap.get(getRecommendationProcessAgentId(process))?.process.id === process.id))
      .subscribe((process: RecommendationProcessModel) => {
        if (process.processStatus === ProcessTrackingStatusEnum.COMPLETED) {
          const agentId: string = getRecommendationProcessAgentId(process);
          this.processesMap.get(agentId)?.unsubscribe();
          this.processesMap.delete(agentId);
          this.store.resetRecommendations();
          this.loadRecommendations();
          this._panel?.instance?.updateProcessingAgents(this.processesMap.keys());
        }
      }));
  }

  ngOnDestroy(): void {
    // Clean up: reset store and unsubscribe from Firebase
    this._clearProcesses();
    this.store.resetRecommendations();
    this._subscriptions.forEach(sub => sub.unsubscribe());
    this._subscriptions = [];
  }

  onSelectAgent(event: Event) {
    if (event) {
      event.stopPropagation();
    }
    this._panel = this.panelService.open(AiAgentSelectComponent, event, {
      processingAgents: Array.from(this.processesMap.keys()),
      showTriggerRule: true,
    }, {
      position: {
        originX: 'center',
        originY: 'bottom'
      }
    });

    if (this._panel) {
      this._panel.instance?.updateProcessingAgents(this.processesMap.keys());

      const agentsLoadedSub = this._panel.instance?.onAgentsLoaded.subscribe(agents => {
        const agentIds = agents.map(agent => agent.id);

        this.recommendationHttpService.getRecommendationProcesses(this._holderId, this._draftId, agentIds)
          .pipe(take(1))
          .subscribe((processes) => {
            processes.forEach(process => {
              this._updateProcess(process);
            });
            this._panel?.instance?.updateProcessingAgents(this.processesMap.keys());
          });
      });

      const agentSelectedSub = this._panel.instance?.onAgentSelected.subscribe((agent) => {
        if (agent) {
          this.isLoading = true;
          this.data.onGenerateRecommendations(agent).then((process: RecommendationProcessModel) => {
            this._updateProcess(process);
            this._panel?.instance?.updateProcessingAgents(this.processesMap.keys());
          }).finally(() => this.isLoading = false);
        }
      });

      const triggerRuleSub = this._panel.instance?.onTriggerRule.subscribe(() => {
        this.isLoading = true;
        this.cdr.detectChanges();

        // Add rule trigger to processes map so it shows a spinner
        this.processesMap.set('triggerRule', {
          process: { id: 'triggerRule', agentId: 'triggerRule', processStatus: ProcessTrackingStatusEnum.IN_PROGRESS } as any,
          unsubscribe: () => {}
        });
        this._panel?.instance?.updateProcessingAgents(Array.from(this.processesMap.keys()));

        this.recommendationHttpService.triggerRuleRecommendations(this._holderId, this._draftId, this._siteId)
          .pipe(
            take(1),
            finalize(() => {
              this.isLoading = false;
              this.cdr.detectChanges();
            })
          )
          .subscribe({
            error: (err) => {
              console.error('Failed to trigger rule recommendations', err);
              this.processesMap.delete('triggerRule');
              this._panel?.instance?.updateProcessingAgents(Array.from(this.processesMap.keys()));
            }
          });
      });

      this._subscriptions.push(this._panel.afterClose$.subscribe(() => {
        agentSelectedSub.unsubscribe();
        agentsLoadedSub.unsubscribe();
        triggerRuleSub.unsubscribe();
      }));
    }
  }

  loadRecommendations(): void {
    // Component Store handles loading, error, Firebase subscription automatically
    this.store.getRecommendations(this._queryParams);
  }

  onFetchNext(): void {
    // Store will use the cursor automatically
    this.store.getRecommendations(this._queryParams);
  }

  closePanel(): void {
    this.onClose.emit();
  }

  updateRecommendationFeedback(recommendation: RecommendationModel): void {
    this.store.updateRecommendation(recommendation);
  }

  markRecommendationAsRead(recommendation: RecommendationModel): void {
    this.store.markRecommendationAsRead(recommendation);
  }

  applyRecommendation(recommendation: RecommendationModel): void {
    this.isLoading = true;
    this.cdr.detectChanges();
    this.recommendationHttpService.invokeRecommendationAction(recommendation.id, this._siteId)
      .pipe(
        take(1),
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (updatedRecommendation) => {
          this.store.onUpdateRecommendation(updatedRecommendation);
          this.recommendationAlertsService.invokeActionSuccess();
          this.markRecommendationAsRead(updatedRecommendation);
        },
        error: (err) => {
          console.error('Failed to apply recommendation action', err);
          this.recommendationAlertsService.invokeActionError();
        }
      });
  }

  private _clearProcesses() {
    for (const value of this.processesMap.values()) {
      value.unsubscribe();
    }

    this.processesMap.clear();
  }

  private _updateProcess(process: RecommendationProcessModel) {
    const agentId: string = getRecommendationProcessAgentId(process);

    if (!this.processesMap.get(agentId)) {
      this.recommendationProcessChannelService.subscribeById(process.id);

      this.processesMap.set(agentId, {
        process: process,
        unsubscribe: () => this.recommendationProcessChannelService.unSubscribeById(process.id)
      });
    }
  }
}

